import React from "react";
import { Box, Tooltip, Typography, Stack, Chip, keyframes } from "@mui/material";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

/**
 * Rotating star that flags a student with a REAL outstanding invoice.
 *
 * The rotation is only on the icon itself — not the parent card, per spec
 * ("Do not rotate the entire student card"). CSS animation is scoped inside
 * a Box wrapper so hover state on siblings never re-triggers it.
 *
 * Tooltip payload varies by permission:
 *   - Finance viewer  → invoice number, status, balance, due date, overdue days
 *   - Everyone else   → generic "Outstanding invoice" warning, no amounts
 *
 * Clicking the icon navigates to the invoice list, deep-linked to the
 * invoice number so the list can highlight/scroll to it. (No dedicated
 * /invoice/:id detail route exists yet; the list is the current entry.)
 */

// 6s rotation, linear — deliberately slow so it draws the eye without
// becoming a distraction. Cross-referenced against the "subtle" spec.
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

export type OutstandingInvoice = {
  id: number;
  invoice_number: string;
  status: string;
  balance_due: number;
  due_date: string | null;
  days_overdue: number;
};

type Props = {
  hasOutstanding: boolean;
  invoice?: OutstandingInvoice; // only present when canSeeFinance is true
  canSeeFinance: boolean;
  size?: number; // px, default 14
};

const money = (n: number) => `$${(Math.round(n * 100) / 100).toFixed(2)}`;

const OutstandingStar: React.FC<Props> = ({ hasOutstanding, invoice, canSeeFinance, size = 14 }) => {
  const navigate = useNavigate();
  if (!hasOutstanding) return null;

  const tooltip = canSeeFinance && invoice ? (
    <Stack spacing={0.4} sx={{ py: 0.5, minWidth: 200 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>
        OUTSTANDING INVOICE
      </Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
        {invoice.invoice_number}
      </Typography>
      <Stack direction="row" justifyContent="space-between">
        <Typography sx={{ fontSize: 11.5 }}>Status</Typography>
        <Chip
          label={invoice.status}
          size="small"
          sx={{ height: 15, fontSize: 9.5, textTransform: "capitalize" }}
        />
      </Stack>
      <Stack direction="row" justifyContent="space-between">
        <Typography sx={{ fontSize: 11.5 }}>Balance Due</Typography>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
          {money(invoice.balance_due)}
        </Typography>
      </Stack>
      {invoice.due_date && (
        <Stack direction="row" justifyContent="space-between">
          <Typography sx={{ fontSize: 11.5 }}>Due</Typography>
          <Typography sx={{ fontSize: 11.5 }}>
            {dayjs(invoice.due_date).format("MMM D, YYYY")}
          </Typography>
        </Stack>
      )}
      {invoice.days_overdue > 0 && (
        <Typography sx={{ fontSize: 11, color: "#fca5a5", fontWeight: 600 }}>
          {invoice.days_overdue} day{invoice.days_overdue === 1 ? "" : "s"} overdue
        </Typography>
      )}
      <Typography sx={{ fontSize: 10, opacity: 0.7, mt: 0.25 }}>
        Click to open invoice
      </Typography>
    </Stack>
  ) : (
    <Stack spacing={0.4} sx={{ py: 0.5 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4 }}>
        OUTSTANDING INVOICE
      </Typography>
      <Typography sx={{ fontSize: 11.5, maxWidth: 220 }}>
        This student has an unpaid balance. Contact Finance for details.
      </Typography>
    </Stack>
  );

  const handleClick = (e: React.MouseEvent) => {
    // Stop the parent card's onClick from also firing (drawer would open).
    e.stopPropagation();
    if (!canSeeFinance) return;
    if (invoice) {
      navigate(`/invoice/list?invoice=${encodeURIComponent(invoice.invoice_number)}`);
    } else {
      navigate("/invoice/list");
    }
  };

  return (
    <Tooltip title={tooltip} arrow placement="top">
      <Box
        component="span"
        role={canSeeFinance ? "button" : undefined}
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: canSeeFinance ? "pointer" : "help",
          lineHeight: 0,
          // Only the icon spins — the Box does not.
          "& svg": {
            fontSize: size,
            color: "#ef4444",
            animation: `${spin} 6s linear infinite`,
            transformOrigin: "50% 50%",
            // Users who set prefers-reduced-motion get a static icon.
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
            },
          },
        }}
      >
        <StarRateRoundedIcon />
      </Box>
    </Tooltip>
  );
};

export default OutstandingStar;
